/**
 * Till peripherals: printer, cash drawer, scale, customer display.
 *
 * **One implementation ships, and it is the browser's own print dialog.**
 * Everything else here is an interface with an honest `supported: false`, and
 * saying so plainly is the point — a screen that offers a "Open drawer" button
 * which silently does nothing is worse than one that does not offer it.
 *
 * Why there are no drivers yet
 * ----------------------------
 * A browser cannot open a raw TCP socket or a USB endpoint on its own, so every
 * real driver is a decision about hardware that has not been made:
 *
 * *   **WebUSB / Web Serial** reach a USB thermal printer directly, but only in
 *     Chrome and Edge, only over HTTPS, and only after a per-device permission
 *     prompt the cashier has to answer.
 * *   **A network printer** on port 9100 needs something outside the browser to
 *     talk to it — a small local agent, or a print server.
 * *   **A Bluetooth printer on Android** — the commonest township till setup —
 *     needs a companion app or a TWA wrapper, because Web Bluetooth does not
 *     expose the serial-port profile these printers use.
 *
 * Picking one before the pilot shops' hardware is known would mean building the
 * wrong one. So this is the shape, and adding a driver means writing one file
 * and registering it — no screen changes.
 *
 * What the print path actually does
 * ---------------------------------
 * `window.print()` against a receipt laid out for 80 mm paper. No paper cut, no
 * drawer kick, no logo — the OS driver owns all of that. It is genuinely what a
 * shop with a driver-installed thermal printer needs, and it works in every
 * browser, which is more than any of the alternatives can say today.
 */

export type PeripheralKind = "printer" | "drawer" | "scale" | "display";

export interface PeripheralStatus {
  kind: PeripheralKind;
  /** Whether anything can actually be done. `false` means the buttons stay hidden. */
  supported: boolean;
  /** Shown to a person when it is not. Plain words, no error codes. */
  reason?: string;
  /** How it is reached, once something is. */
  transport?: "browser-print" | "webusb" | "webserial" | "bridge" | "companion-app";
}

export interface ReceiptPayload {
  receiptNumber: string;
  merchantName: string;
  lines: {
    description: string;
    quantity: string;
    unitPrice: string;
    lineTotal: string;
  }[];
  subtotal: string;
  taxTotal: string;
  total: string;
  tendered?: string;
  change?: string;
  method: string;
  cashier: string;
  when: string;
  /** True while the sale is still only on this device. Printed, deliberately. */
  queued?: boolean;
}

export interface Printer {
  status(): PeripheralStatus;
  /** Resolves once the print dialog has been handed the document. */
  print(receipt: ReceiptPayload): Promise<void>;
}

export interface CashDrawer {
  status(): PeripheralStatus;
  open(): Promise<void>;
}

export interface Scale {
  status(): PeripheralStatus;
  /** A weight in kilograms, as a decimal string — never a float. */
  read(): Promise<string>;
}

export interface CustomerDisplay {
  status(): PeripheralStatus;
  show(lines: string[]): Promise<void>;
}

/**
 * A peripheral that is not connected to anything.
 *
 * Used for the drawer, the scale and the customer display until somebody
 * decides what hardware the shops have. Every method rejects with the same
 * sentence its `status()` gives, so a caller that ignores `supported` still
 * fails loudly rather than appearing to work.
 */
function unavailable(kind: PeripheralKind, reason: string) {
  const status = (): PeripheralStatus => ({ kind, supported: false, reason });
  const refuse = () => Promise.reject(new Error(reason));
  return { status, refuse };
}

/**
 * The printer: the browser's print dialog, against an 80 mm stylesheet.
 *
 * `window.print()` is synchronous and blocking in every browser, and there is no
 * event for "the user pressed Cancel" — so this resolves once the dialog has
 * been shown, not once paper has moved. A caller must not treat a resolved
 * promise as proof anything was printed, which is why nothing does.
 */
export const browserPrinter: Printer = {
  status: () => ({
    kind: "printer",
    supported: true,
    transport: "browser-print",
    reason:
      "Prints through the browser to whichever printer this device has installed. " +
      "No automatic paper cut or drawer kick — those need a driver this build does not have.",
  }),

  print: () => {
    // The receipt is already on the page inside `[data-print="receipt"]`; the
    // print stylesheet hides everything else. Rendering a second copy into an
    // iframe would be tidier and would lose the React state it is built from.
    //
    // Resolves once the dialog has been *shown*, not once paper has moved —
    // `window.print()` is blocking and there is no event for "the user pressed
    // Cancel". No caller may treat a resolved promise as proof of a printed
    // receipt, which is why none does.
    window.print();
    return Promise.resolve();
  },
};

const drawerStub = unavailable(
  "drawer",
  "No cash drawer is connected. A drawer opens on a pulse from a printer, so this " +
    "needs the printer driver first — see src/peripherals/index.ts.",
);

export const cashDrawer: CashDrawer = {
  status: drawerStub.status,
  open: drawerStub.refuse,
};

const scaleStub = unavailable(
  "scale",
  "No scale is connected. Weighed items are keyed in by hand until one is.",
);

export const scale: Scale = {
  status: scaleStub.status,
  read: scaleStub.refuse,
};

const displayStub = unavailable(
  "display",
  "No customer display is connected.",
);

export const customerDisplay: CustomerDisplay = {
  status: displayStub.status,
  show: displayStub.refuse,
};

/** Everything, for the diagnostics screen. */
export function peripheralStatuses(): PeripheralStatus[] {
  return [
    browserPrinter.status(),
    cashDrawer.status(),
    scale.status(),
    customerDisplay.status(),
  ];
}
